/**
 * WebAudio graph for one <video> element, owned for the whole page session.
 *
 * A media element may be routed into WebAudio at most once: asking for a
 * second source node throws InvalidStateError and leaves that element's audio
 * permanently broken. Since the manager now outlives the sphere surface — it
 * is created lazily, kept across view switches (bypassed while in quadrant
 * view) and disposed only when the video element itself goes away — the graph
 * is cached per element so a repeat construction reuses it instead of
 * re-routing the element.
 */
interface ElementAudioGraph {
  context: AudioContext;
  source: MediaElementAudioSourceNode;
  panner: PannerNode;
  gain: GainNode;
  disposed: boolean;
}

const graphs = new WeakMap<HTMLVideoElement, ElementAudioGraph>();

function acquireGraph(videoElement: HTMLVideoElement): ElementAudioGraph {
  const existing = graphs.get(videoElement);
  if (existing) {
    if (existing.disposed) {
      // Nothing can revive it: the element is already bound to a source node
      // whose context is closed. Callers construct inside a try/catch and
      // degrade to plain element audio.
      throw new Error(
        'SpatialAudioManager: audio graph for this video element was already disposed'
      );
    }
    return existing;
  }

  const context = new AudioContext();
  const source = context.createMediaElementSource(videoElement);

  const panner = context.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 1;
  panner.maxDistance = 10;
  panner.rolloffFactor = 1;
  panner.coneInnerAngle = 360;
  panner.coneOuterAngle = 360;
  panner.coneOuterGain = 1;

  const gain = context.createGain();

  const graph: ElementAudioGraph = { context, source, panner, gain, disposed: false };
  graphs.set(videoElement, graph);
  return graph;
}

export class SpatialAudioManager {
  private graph: ElementAudioGraph;
  private audioContext: AudioContext;
  private sourceNode: MediaElementAudioSourceNode;
  private pannerNode: PannerNode;
  private gainNode: GainNode;
  private enabled = false;
  private savedVolume = 1;

  // Target panner position (updated on speaker change)
  private targetX = 0;
  private targetZ = -1;

  constructor(videoElement: HTMLVideoElement) {
    this.graph = acquireGraph(videoElement);
    this.audioContext = this.graph.context;
    this.sourceNode = this.graph.source;
    this.pannerNode = this.graph.panner;
    this.gainNode = this.graph.gain;

    // Start bypassed (spatial audio disabled by default). A reused graph may
    // have been left routed through the panner, so rewire from scratch.
    this.sourceNode.disconnect();
    this.pannerNode.disconnect();
    this.sourceNode.connect(this.gainNode);
    this.gainNode.connect(this.audioContext.destination);

    // Set listener at origin facing forward
    const listener = this.audioContext.listener;
    if (listener.positionX) {
      listener.positionX.value = 0;
      listener.positionY.value = 0;
      listener.positionZ.value = 0;
      listener.forwardX.value = 0;
      listener.forwardY.value = 0;
      listener.forwardZ.value = -1;
      listener.upX.value = 0;
      listener.upY.value = 1;
      listener.upZ.value = 0;
    } else {
      // Fallback for older browsers
      listener.setPosition(0, 0, 0);
      listener.setOrientation(0, 0, -1, 0, 1, 0);
    }
  }

  resumeContext(): void {
    if (this.graph.disposed) return;
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  updateListenerOrientation(yawDegrees: number): void {
    if (!this.enabled || this.graph.disposed) return;

    const yawRad = (yawDegrees * Math.PI) / 180;
    const forwardX = -Math.sin(yawRad);
    const forwardZ = -Math.cos(yawRad);

    const listener = this.audioContext.listener;
    if (listener.forwardX) {
      const t = this.audioContext.currentTime;
      listener.forwardX.setValueAtTime(forwardX, t);
      listener.forwardZ.setValueAtTime(forwardZ, t);
    } else {
      listener.setOrientation(forwardX, 0, forwardZ, 0, 1, 0);
    }
  }

  setActiveSpeaker(positionDegrees: number | null): void {
    if (this.graph.disposed) return;

    if (positionDegrees === null) {
      // No speaker — center audio (place at listener origin direction, no spatial effect)
      this.targetX = 0;
      this.targetZ = 0;

      // Move panner to origin so there's no directional effect
      if (this.enabled) {
        const t = this.audioContext.currentTime + 0.15;
        this.pannerNode.positionX.linearRampToValueAtTime(0, t);
        this.pannerNode.positionY.linearRampToValueAtTime(0, t);
        this.pannerNode.positionZ.linearRampToValueAtTime(0, t);
      }
      return;
    }

    const rad = (positionDegrees * Math.PI) / 180;
    this.targetX = Math.sin(rad);
    this.targetZ = -Math.cos(rad);

    if (this.enabled) {
      const t = this.audioContext.currentTime + 0.15;
      this.pannerNode.positionX.linearRampToValueAtTime(this.targetX, t);
      this.pannerNode.positionY.linearRampToValueAtTime(0, t);
      this.pannerNode.positionZ.linearRampToValueAtTime(this.targetZ, t);
    }
  }

  setVolume(volume: number): void {
    this.savedVolume = volume;
    if (this.graph.disposed) return;
    this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
  }

  setMuted(muted: boolean): void {
    if (this.graph.disposed) return;
    this.gainNode.gain.setValueAtTime(
      muted ? 0 : this.savedVolume,
      this.audioContext.currentTime
    );
  }

  /**
   * Enable/disable spatialisation. This — not dispose() — is what a view
   * switch calls: disabled means the element's audio runs straight through
   * source → gain → destination, so it stays audible in quadrant view.
   */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled || this.graph.disposed) return;
    this.enabled = enabled;

    // Rewire the audio graph
    this.sourceNode.disconnect();
    this.pannerNode.disconnect();

    if (enabled) {
      // source → panner → gain → destination (HRTF panner already produces
      // correct binaural L/R; no channel swap)
      this.sourceNode.connect(this.pannerNode);
      this.pannerNode.connect(this.gainNode);

      // Set panner to current target position
      const t = this.audioContext.currentTime;
      this.pannerNode.positionX.setValueAtTime(this.targetX, t);
      this.pannerNode.positionY.setValueAtTime(0, t);
      this.pannerNode.positionZ.setValueAtTime(this.targetZ, t);
    } else {
      // source → gain → destination (bypass panner)
      this.sourceNode.connect(this.gainNode);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Tear down the element's audio graph for good. Only the owner of the video
   * element calls this, when the element itself is going away — the manager's
   * lifetime is the element's, not a surface mount's. Use setEnabled(false)
   * to stop spatialising; the graph cannot be rebuilt once closed.
   */
  dispose(): void {
    if (this.graph.disposed) return;
    this.graph.disposed = true;
    this.sourceNode.disconnect();
    this.pannerNode.disconnect();
    this.gainNode.disconnect();
    this.audioContext.close();
  }
}
